import { Browser } from "@capacitor/browser";
import { NativeMarket } from "@capgo/native-market";
import { toast } from "react-toastify";

import {
  APP_VERSION_NOT_PUBLISHED_TOAST,
  APP_VERSION_TOAST_CONTAINER_ID,
} from "../constants/appVersion";

const showNotPublishedToast = () => {
  toast.info(APP_VERSION_NOT_PUBLISHED_TOAST, {
    containerId: APP_VERSION_TOAST_CONTAINER_ID,
  });
};

interface OpenAppStoreOptions {
  storeUrl?: string | null;
  appId?: string | null;
  useNativeMarket?: boolean;
}

const openWithBrowser = async (storeUrl: string | null | undefined): Promise<boolean> => {
  if (!storeUrl) return false;
  try {
    await Browser.open({ url: storeUrl });
    return true;
  } catch {
    return false;
  }
};

// Resolves which store-opening primitive to use:
//   - useNativeMarket + appId → NativeMarket.openStoreListing.
//     iOS: plugin does an iTunes Lookup with the bundle id → opens
//     itms-apps://...; rejects (and we fall back to storeUrl) when the
//     app is not yet in App Store (TestFlight phase).
//     Android: plugin opens market://details?id=...; if multiple stores
//     are installed (Play + GetApps + Amazon) the user sees an "Open
//     with" chooser. The plugin does not expose a way to force Play.
//   - storeUrl fallback → @capacitor/browser (TestFlight invites,
//     Play Store HTTPS URLs, etc.).
//   - Anything failing → toast as last resort.
//
// Pass the bundle/package id (e.g. `ar.com.allaria.app`) as `appId` for
// both platforms. Set `useNativeMarket: false` until both stores have
// public listings — until then the storeUrl fallback handles testing
// tracks (TestFlight, Play Internal Testing).
export const openAppStore = async (options: OpenAppStoreOptions | null | undefined): Promise<void> => {
  if (!options) {
    showNotPublishedToast();
    return;
  }

  if (options.useNativeMarket && options.appId) {
    try {
      await NativeMarket.openStoreListing({ appId: options.appId, country: "ar" });
      return;
    } catch {
      // Intentional fall-through to storeUrl. iTunes Lookup may return
      // zero results in TestFlight phase, or the OS may refuse to open
      // the store URL — try the storeUrl fallback below.
    }
  }

  if (await openWithBrowser(options.storeUrl)) return;

  showNotPublishedToast();
};
